/**
 * Space on a library row: the full description (the list truncates it),
 * status, pages, where it was used, the date and every tag, plus the one
 * follow-up the row only hints at (a conversion to check, another client's
 * details to deal with).
 */
import {
  ArrowUpRight,
  Briefcase,
  ShieldAlert,
  TriangleAlert,
  UserRound
} from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  PeekAction,
  PeekActions,
  PeekPanel,
  PeekProp,
  PeekProps
} from "@/components/ui/peek-panel";
import { Tag } from "@/components/ui/tag";
import { formatMdY } from "@/features/clientProfile/lib/format";

import {
  CATEGORY_LABEL,
  categoryOf,
  CONVERSION_BADGE_LABEL,
  CONVERSION_BADGE_TITLE,
  conversionBadgeOf,
  hasOpenClientDetails,
  usageLabel
} from "../model";
import type { FirmLibraryEntry } from "../types";
import {
  ClientDetailsBadge,
  NotReviewedBadge,
  ReadinessBadge
} from "./EntryBadges";
import { LibraryFileIcon } from "./LibraryFileIcon";

type Props = {
  entry: FirmLibraryEntry;
  onOpen: () => void;
};

export const LibraryPeek: FC<Props> = ({ entry, onOpen }) => {
  const title = entry.name || entry.original_filename || "Untitled";
  const conversion = conversionBadgeOf(entry);
  const needsCheck =
    conversion === "check" ||
    conversion === "review" ||
    conversion === "unused";
  const clientDetails = hasOpenClientDetails(entry);
  const clientMatch = entry.client_details?.client_match ?? null;
  const visas = entry.visas ?? [];

  return (
    <PeekPanel
      aria-label={`${title} preview`}
      className="w-95"
      data-testid="library-peek"
    >
      <div className="flex items-center gap-2">
        <LibraryFileIcon entry={entry} />
        <span className="text-sm text-fg-3">
          {CATEGORY_LABEL[categoryOf(entry)]}
        </span>
        <span className="flex-1" />
        <Button size="xs" onClick={onOpen}>
          Open
          <ArrowUpRight />
        </Button>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-lg font-medium text-fg">{title}</span>
        {entry.description ? (
          <p className="text-base text-fg-2">{entry.description}</p>
        ) : null}
      </div>

      <PeekProps>
        <PeekProp label="Status">
          <ReadinessBadge entry={entry} />
          <NotReviewedBadge entry={entry} />
          <ClientDetailsBadge entry={entry} />
        </PeekProp>
        <PeekProp label="Pages">
          <span className="tabular-nums">
            {entry.page_count == null ? "—" : entry.page_count}
          </span>
        </PeekProp>
        <PeekProp label="Used">
          {usageLabel(entry) ? (
            <span className="flex items-center gap-1.5">
              <Briefcase className="size-3.5 text-fg-3" />
              <span data-testid="peek-usage">{usageLabel(entry)}</span>
            </span>
          ) : (
            <span className="text-fg-4">Not used yet</span>
          )}
        </PeekProp>
        {visas.length ? (
          <PeekProp label="Visa">
            {visas.map((v) => (
              <Tag key={v}>{v}</Tag>
            ))}
          </PeekProp>
        ) : null}
        <PeekProp label="Updated">
          <span className="tabular-nums">
            {formatMdY(entry.updated_at ?? entry.created_at)}
          </span>
        </PeekProp>
        {entry.tags.length ? (
          <PeekProp label="Tags" align="start">
            {entry.tags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </PeekProp>
        ) : null}
      </PeekProps>

      {(needsCheck && conversion) || clientDetails ? (
        <PeekActions>
          {needsCheck && conversion ? (
            <PeekAction
              icon={TriangleAlert}
              tone="warn"
              title={CONVERSION_BADGE_LABEL[conversion]}
              description={CONVERSION_BADGE_TITLE[conversion]}
              action={
                <Button size="xs" onClick={onOpen}>
                  Check
                </Button>
              }
              data-testid="library-peek-conversion"
            />
          ) : null}
          {clientDetails && clientMatch ? (
            <PeekAction
              icon={UserRound}
              tone="warn"
              title="Looks Like Case Evidence"
              description={`It names ${clientMatch.name}, one of your clients. File it on their case; the library is for samples other cases can reuse.`}
              action={
                <Button asChild size="xs">
                  <Link to={`/clients/${encodeURIComponent(clientMatch.id)}`}>
                    Open Client
                  </Link>
                </Button>
              }
              data-testid="library-peek-client-match"
            />
          ) : clientDetails ? (
            <PeekAction
              icon={ShieldAlert}
              tone="warn"
              title="Another Client's Details"
              description="The agents never copy them into a case. Review them to redact or keep."
              action={
                <Button size="xs" onClick={onOpen}>
                  Review
                </Button>
              }
            />
          ) : null}
        </PeekActions>
      ) : null}
    </PeekPanel>
  );
};
